import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import { PipelineKonva } from "@project/floorplanner/konva/pipeline-konva";
import Konva from "konva";
import { Text } from "konva/lib/shapes/Text";
import Group = Konva.Group;
import Ellipse = Konva.Ellipse;
import Rect = Konva.Rect;
import Path = Konva.Path;

export class ProtectiveTubeKonva {
  static readonly COLOR_CODE = "#556C7CCC";

  static init(protectiveTube: ProtectiveTube): Group {
    const parentGroup = new Konva.Group();

    const protectiveTubeGroup = new Konva.Group({
      x: protectiveTube.x,
      y: protectiveTube.y,
      rotation: protectiveTube.rotation,
      material: protectiveTube.material,
      draggable: false,
    });
    protectiveTubeGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, protectiveTube);
    protectiveTubeGroup.setAttr("minWidth", 50);
    protectiveTubeGroup.setAttr("minHeight", 20);

    const mid = new Konva.Rect({
      fill: ProtectiveTubeKonva.COLOR_CODE,
    });

    const front = new Konva.Ellipse({
      radiusX: 0, // actual value set in setShapeDimensions
      radiusY: 0, // actual value set in setShapeDimensions
      fill: "white",
      stroke: ProtectiveTubeKonva.COLOR_CODE,
      strokeWidth: 0.5,
    });

    const back = new Konva.Path({
      fill: ProtectiveTubeKonva.COLOR_CODE,
    });

    PipelineKonva.setShapeDimensions(protectiveTube.width, protectiveTube.height, protectiveTubeGroup, mid, front, back);

    const itemText = FloorplanItemTextKonva.init(protectiveTube);
    const text = FloorplanItemTextKonva.getText(itemText);
    this.scaleText(text, protectiveTubeGroup);
    FloorplanItemTextKonva.updateBackground(itemText);

    protectiveTubeGroup.on("transformstart", () => {
      itemText.hide();
    });

    protectiveTubeGroup.on("transform", () => {
      const width = protectiveTubeGroup.width() * protectiveTubeGroup.scaleX();
      const height = protectiveTubeGroup.height() * protectiveTubeGroup.scaleY();

      PipelineKonva.setShapeDimensions(width, height, protectiveTubeGroup, mid, front, back);

      protectiveTubeGroup.scaleX(1);
      protectiveTubeGroup.scaleY(1);
    });

    protectiveTubeGroup.on("transformend", () => {
      protectiveTube.updateForm(
        protectiveTubeGroup.width(),
        protectiveTubeGroup.height(),
        protectiveTubeGroup.rotation(),
        protectiveTubeGroup.x(),
        protectiveTubeGroup.y(),
      );
      this.scaleText(text, protectiveTubeGroup);
      FloorplanItemTextKonva.updateBackground(itemText);
      this.setTextPosition(itemText, protectiveTubeGroup);
      const labelHidden = itemText.getAttr("labelHidden") === true;
      if (!labelHidden) {
        itemText.show();
      }
    });

    protectiveTubeGroup.on("dragmove", () => {
      this.setTextPosition(itemText, protectiveTubeGroup);
    });

    protectiveTubeGroup.add(mid, front, back, itemText);

    parentGroup.add(protectiveTubeGroup, itemText);
    this.setTextPosition(itemText, protectiveTubeGroup);

    return parentGroup;
  }

  static refreshLength(protectiveTube: ProtectiveTube, parentGroup: Group) {
    const protectiveTubeGroup = parentGroup.getChildren()[0] as Group;
    const mid = protectiveTubeGroup.getChildren()[0] as Rect;
    const front = protectiveTubeGroup.getChildren()[1] as Ellipse;
    const back = protectiveTubeGroup.getChildren()[2] as Path;
    PipelineKonva.setShapeDimensions(protectiveTube.width, protectiveTube.height, protectiveTubeGroup, mid, front, back);
    this.refreshItemText(protectiveTube, parentGroup);
  }

  static refreshItemText(protectiveTube: ProtectiveTube, group: Group) {
    const positionId = group.getChildren()[1] as Group;
    const protectiveTubeGroup = group.getChildren()[0] as Konva.Group;
    FloorplanItemTextKonva.refreshText(protectiveTube, positionId);
    PipelineKonva.setTextPosition(positionId, protectiveTubeGroup);
  }

  private static setTextPosition(positionId: Group, group: Group): void {
    const item = group.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    LabelLayoutKonva.placeLabelForItem(positionId, group, item);
  }

  private static scaleText(itemText: Text, tubeGroup: Group) {
    itemText.fontSize(tubeGroup.height() / 3);
  }
}
